import React, {useState, useEffect} from 'react'
import Axios from 'axios'

// Detect if we're running locally or on production
const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'

const getBackendUrl = () => {
  if (!isLocalDev) return 'https://moxtest.app'
  return `http://localhost:${__DEV_BACKEND_PORT__}`
}

const Admin = () => {
  const [password, setPassword] = useState('')
  const [token, setToken] = useState(localStorage.getItem('adminToken'))
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  
  // Price history processing state
  const [processingPrices, setProcessingPrices] = useState(false)
  const [priceResult, setPriceResult] = useState(null)
  const [priceError, setPriceError] = useState(null)
  const [downloadingPrices, setDownloadingPrices] = useState(false)
  const [downloadStatus, setDownloadStatus] = useState('')
  
  // Set price refresh state
  const [refreshingSetPrices, setRefreshingSetPrices] = useState(false)
  const [refreshPriceResult, setRefreshPriceResult] = useState(null)
  const [refreshingSet, setRefreshingSet] = useState(null) // which individual set is being refreshed
  const [downloadingSet, setDownloadingSet] = useState(null) // which set is downloading images
  const [refreshingSetIds, setRefreshingSetIds] = useState(null) // which set is refreshing scryfallIds
  const [refreshingSetCards, setRefreshingSetCards] = useState(null) // which set is refreshing cards from Scryfall
  const [refreshCardsResult, setRefreshCardsResult] = useState(null)

  // Load stats when authenticated
  useEffect(() => {
    if (token) {
      loadStats()
    }
  }, [token])

  const handleLogin = async () => {
    setError(null)
    setLoading(true)

    try {
      const backendUrl = getBackendUrl()
      const res = await Axios.post(`${backendUrl}/admin/login`, {password})

      if (res.data.success) {
        localStorage.setItem('adminToken', res.data.token)
        setToken(res.data.token)
      }
    } catch (err) {
      setError('Invalid password')
    } finally {
      setLoading(false)
    }
  }

  const handleLogout = () => {
    localStorage.removeItem('adminToken')
    setToken(null)
    setStats(null)
  }

  const loadStats = async () => {
    setLoading(true)
    setError(null)

    try {
      const backendUrl = getBackendUrl()
      const res = await Axios.get(`${backendUrl}/admin/stats`, {
        headers: {'x-admin-token': token},
      })
      setStats(res.data)
    } catch (err) {
      if (err.response?.status === 401) {
        handleLogout()
        setError('Session expired. Please login again.')
      } else {
        setError('Failed to load stats')
      }
    } finally {
      setLoading(false)
    }
  }

  // Login screen
  if (!token) {
    return (
      <div
        style={{
          background: '#1a1a1a',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 20,
        }}>
        <div
          style={{
            background: '#222',
            padding: 32,
            borderRadius: 12,
            maxWidth: 400,
            width: '100%',
          }}>
          <h1 style={{color: '#fff', fontSize: 24, margin: 0, marginBottom: 24, textAlign: 'center'}}>Admin Login</h1>

          <input
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleLogin()}
            placeholder="Password"
            style={{
              width: '100%',
              padding: 12,
              fontSize: 16,
              border: 'none',
              borderRadius: 8,
              background: '#333',
              color: '#fff',
              marginBottom: 16,
              boxSizing: 'border-box',
            }}
          />

          <button
            onClick={handleLogin}
            disabled={loading}
            style={{
              width: '100%',
              padding: 12,
              fontSize: 16,
              border: 'none',
              borderRadius: 8,
              background: '#4a4',
              color: '#fff',
              cursor: loading ? 'wait' : 'pointer',
              opacity: loading ? 0.7 : 1,
            }}>
            {loading ? 'Loading...' : 'Login'}
          </button>

          {error && <div style={{color: '#f55', marginTop: 16, textAlign: 'center'}}>{error}</div>}

          <a
            href="/previews"
            style={{
              display: 'block',
              marginTop: 24,
              textAlign: 'center',
              color: '#888',
              textDecoration: 'none',
            }}>
            ← Back to Previews
          </a>
        </div>
      </div>
    )
  }

  // Stats dashboard
  return (
    <div
      style={{
        background: '#1a1a1a',
        minHeight: '100vh',
        height: '100vh',
        padding: 24,
        overflowY: 'scroll',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
      }}>
      <div style={{maxWidth: 1000, margin: '0 auto'}}>
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 32,
          }}>
          <h1 style={{color: '#fff', fontSize: 28, margin: 0}}>Admin Dashboard</h1>
          <div style={{display: 'flex', gap: 12}}>
            <button
              onClick={loadStats}
              disabled={loading}
              style={{
                padding: '8px 16px',
                border: 'none',
                borderRadius: 6,
                background: '#444',
                color: '#fff',
                cursor: 'pointer',
              }}>
              Refresh
            </button>
            <button
              onClick={handleLogout}
              style={{
                padding: '8px 16px',
                border: 'none',
                borderRadius: 6,
                background: '#633',
                color: '#fff',
                cursor: 'pointer',
              }}>
              Logout
            </button>
          </div>
        </div>

        {loading && !stats && (
          <div style={{color: '#888', textAlign: 'center', padding: 40}}>Loading stats...</div>
        )}

        {error && <div style={{color: '#f55', textAlign: 'center', padding: 20}}>{error}</div>}

        {stats && (
          <div style={{display: 'grid', gap: 24}}>
            {/* Disk Usage */}
            {stats.disk && (
              <div style={{background: '#222', padding: 24, borderRadius: 12}}>
                <h2 style={{color: '#fff', fontSize: 18, margin: 0, marginBottom: 4}}>System Disk Usage</h2>
                <div style={{color: '#666', fontSize: 12, marginBottom: 16}}>
                  (Your server's total disk - not just cards)
                </div>
                <div style={{marginBottom: 16}}>
                  <div
                    style={{
                      background: '#333',
                      borderRadius: 8,
                      height: 24,
                      overflow: 'hidden',
                    }}>
                    <div
                      style={{
                        background: parseInt(stats.disk.usePercent) > 90 
                          ? 'linear-gradient(90deg, #a44, #c66)' 
                          : 'linear-gradient(90deg, #4a4, #6c6)',
                        height: '100%',
                        width: stats.disk.usePercent,
                        transition: 'width 0.3s',
                      }}
                    />
                  </div>
                  <div style={{display: 'flex', justifyContent: 'space-between', marginTop: 8}}>
                    <span style={{color: '#888', fontSize: 12}}>Used: {stats.disk.usedFormatted}</span>
                    <span style={{color: parseInt(stats.disk.usePercent) > 90 ? '#f55' : '#888', fontSize: 12, fontWeight: 'bold'}}>
                      {stats.disk.usePercent}
                    </span>
                    <span style={{color: '#888', fontSize: 12}}>Total: {stats.disk.totalFormatted}</span>
                  </div>
                </div>
                <div style={{color: '#aaa', fontSize: 14}}>
                  Available: <strong style={{color: parseInt(stats.disk.usePercent) > 90 ? '#f55' : '#4a4'}}>
                    {stats.disk.availableFormatted}
                  </strong>
                  {parseInt(stats.disk.usePercent) > 90 && (
                    <span style={{color: '#f55', marginLeft: 8}}>⚠️ Low disk space!</span>
                  )}
                </div>
              </div>
            )}

            {/* Cards Storage */}
            <div style={{background: '#222', padding: 24, borderRadius: 12}}>
              <h2 style={{color: '#fff', fontSize: 18, margin: 0, marginBottom: 4}}>Cards Storage</h2>
              <div style={{color: '#666', fontSize: 12, marginBottom: 16}}>
                Your downloaded MTG card images
              </div>
              <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 16}}>
                <StatBox label="Cards Folder Size" value={stats.cardsFolder.totalSizeFormatted} highlight />
                <StatBox label="Image Files" value={stats.cardsFolder.imageFiles.toLocaleString()} />
                <StatBox label="Metadata Files" value={stats.cardsFolder.metadataFiles} />
                <StatBox label="% of Total Disk" value={stats.cardsPercentOfDisk || 'N/A'} small />
              </div>
            </div>

            {/* Database Stats */}
            <div style={{background: '#222', padding: 24, borderRadius: 12}}>
              <h2 style={{color: '#fff', fontSize: 18, margin: 0, marginBottom: 16}}>Database</h2>
              <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 16}}>
                <StatBox label="Total Sets" value={stats.database.totalSets} />
                <StatBox label="Total Cards" value={stats.database.totalCards.toLocaleString()} />
                <StatBox label="Avg Cards/Set" value={stats.database.avgCardsPerSet} />
                <StatBox label="Avg Image Size" value={stats.database.avgImageSize} />
              </div>
            </div>

            {/* Estimates */}
            <div style={{background: '#222', padding: 24, borderRadius: 12}}>
              <h2 style={{color: '#fff', fontSize: 18, margin: 0, marginBottom: 16}}>Full DB Estimates</h2>
              <div style={{marginBottom: 16}}>
                <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: 8}}>
                  <span style={{color: '#888'}}>Progress to full MTG database:</span>
                  <span style={{color: '#4a4', fontWeight: 'bold'}}>{stats.estimates.currentProgress}</span>
                </div>
                <div
                  style={{
                    background: '#333',
                    borderRadius: 8,
                    height: 16,
                    overflow: 'hidden',
                  }}>
                  <div
                    style={{
                      background: 'linear-gradient(90deg, #48f, #84f)',
                      height: '100%',
                      width: stats.estimates.currentProgress,
                      transition: 'width 0.3s',
                    }}
                  />
                </div>
              </div>
              <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 16}}>
                <StatBox label="Est. Total Cards" value={stats.estimates.totalMtgCards.toLocaleString()} />
                <StatBox label="Est. Full Size" value={stats.estimates.estimatedFullDbSize} />
                <StatBox label="Remaining Cards" value={stats.estimates.remainingCards.toLocaleString()} />
                <StatBox label="Est. Remaining" value={stats.estimates.estimatedRemainingSize} />
              </div>
            </div>

            {/* Price History Import */}
            <div style={{background: '#222', padding: 24, borderRadius: 12}}>
              <h2 style={{color: '#fff', fontSize: 18, margin: 0, marginBottom: 4}}>Price History Import</h2>
              <div style={{color: '#666', fontSize: 12, marginBottom: 16}}>
                Bulk import 90 days of price history from MTGJSON for ALL cards
              </div>
              
              <div style={{marginBottom: 16, padding: 12, background: '#2a2a2a', borderRadius: 8, fontSize: 13, color: '#aaa'}}>
                Click "Download" to fetch the latest MTGJSON files, then "Process" to import price history.
              </div>

              {downloadStatus && (
                <div style={{marginBottom: 12, padding: 10, background: '#234', borderRadius: 6, color: '#8cf', fontSize: 12}}>
                  {downloadStatus}
                </div>
              )}

              <div style={{display: 'flex', gap: 12, flexWrap: 'wrap'}}>
                {/* Download Button */}
                <button
                  onClick={async () => {
                    setDownloadingPrices(true)
                    setDownloadStatus('Starting download...')
                    setPriceResult(null)
                    setPriceError(null)
                    try {
                      const backendUrl = getBackendUrl()
                      const res = await Axios.post(`${backendUrl}/admin/download-mtgjson`, {}, {
                        headers: {'x-admin-token': token},
                        timeout: 600000, // 10 minute timeout for download
                      })
                      setDownloadStatus(`✅ ${res.data.message}`)
                    } catch (err) {
                      setDownloadStatus(`❌ Download failed: ${err.response?.data?.message || err.message}`)
                    } finally {
                      setDownloadingPrices(false)
                    }
                  }}
                  disabled={downloadingPrices || processingPrices}
                  style={{
                    padding: '12px 24px',
                    fontSize: 14,
                    border: 'none',
                    borderRadius: 8,
                    background: downloadingPrices ? '#555' : '#48f',
                    color: '#fff',
                    cursor: (downloadingPrices || processingPrices) ? 'wait' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}>
                  {downloadingPrices ? (
                    <>
                      <span style={{
                        width: 16,
                        height: 16,
                        border: '2px solid #fff',
                        borderTopColor: 'transparent',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                      }} />
                      Downloading...
                    </>
                  ) : (
                    '📥 Download MTGJSON'
                  )}
                </button>

                {/* Process Button */}
                <button
                onClick={async () => {
                  setProcessingPrices(true)
                  setPriceResult(null)
                  setPriceError(null)
                  try {
                    const backendUrl = getBackendUrl()
                    const res = await Axios.post(`${backendUrl}/admin/process-allprices`, {}, {
                      headers: {'x-admin-token': token},
                      timeout: 900000, // 15 minute timeout for large file
                    })
                    setPriceResult(res.data)
                  } catch (err) {
                    setPriceError(err.response?.data?.message || err.message)
                  } finally {
                    setProcessingPrices(false)
                  }
                }}
                disabled={processingPrices || downloadingPrices}
                style={{
                  padding: '12px 24px',
                  fontSize: 14,
                  border: 'none',
                  borderRadius: 8,
                  background: (processingPrices || downloadingPrices) ? '#555' : '#4a4',
                  color: '#fff',
                  cursor: (processingPrices || downloadingPrices) ? 'wait' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}>
                {processingPrices ? (
                  <>
                    <span style={{
                      width: 16,
                      height: 16,
                      border: '2px solid #fff',
                      borderTopColor: 'transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                    }} />
                    Processing (this may take a few minutes)...
                  </>
                ) : (
                  '📊 Process AllPrices.json'
                )}
              </button>
              </div>

              {priceError && (
                <div style={{marginTop: 16, padding: 12, background: '#422', borderRadius: 8, color: '#f88'}}>
                  ❌ {priceError}
                </div>
              )}

              {priceResult && (
                <div style={{marginTop: 16, padding: 16, background: '#234', borderRadius: 8}}>
                  <div style={{color: '#4f4', fontWeight: 'bold', marginBottom: 12}}>✅ {priceResult.message}</div>
                  <div style={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12}}>
                    <div style={{color: '#aaa'}}>Cards in identifiers: <strong style={{color: '#fff'}}>{priceResult.totalCardsInIdentifiers?.toLocaleString()}</strong></div>
                    <div style={{color: '#aaa'}}>Sets with history: <strong style={{color: '#fff'}}>{priceResult.setsProcessed}</strong></div>
                    <div style={{color: '#aaa'}}>Price entries scanned: <strong style={{color: '#fff'}}>{priceResult.totalCardsInFile?.toLocaleString()}</strong></div>
                    <div style={{color: '#aaa'}}>Cards with history: <strong style={{color: '#4f4'}}>{priceResult.cardsWithPriceHistory?.toLocaleString()}</strong></div>
                  </div>
                </div>
              )}
            </div>

            {/* Top Sets */}
            <div style={{background: '#222', padding: 24, borderRadius: 12}}>
              <h2 style={{color: '#fff', fontSize: 18, margin: 0, marginBottom: 16}}>
                Loaded Sets ({stats.topSets.length})
              </h2>
              <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
                {stats.topSets.map((set, i) => {
                  const hasPriceIssue = set.pricePercent < 50
                  const hasImageIssue = set.imagePercent < 100
                  const isRefreshing = refreshingSet === set.setCode
                  const isDownloading = downloadingSet === set.setCode
                  return (
                    <div
                      key={set.setCode}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '8px 12px',
                        background: (hasPriceIssue || hasImageIssue) ? '#3a2a2a' : '#2a2a2a',
                        borderRadius: 6,
                        border: hasImageIssue ? '1px solid #a63' : hasPriceIssue ? '1px solid #633' : 'none',
                      }}>
                      <span style={{color: '#fff', flex: 1}}>
                        {i + 1}. {set.setName}{' '}
                        <span style={{color: '#666'}}>({set.setCode.toUpperCase()})</span>
                      </span>
                      <span style={{color: '#4a4', fontWeight: 'bold', marginRight: 12}}>
                        {set.cardCount} cards
                      </span>
                      {/* Image status */}
                      <span 
                        style={{
                          color: set.imagePercent === 100 ? '#4a4' : set.imagePercent >= 50 ? '#a84' : '#f55',
                          fontSize: 11,
                          minWidth: 65,
                          textAlign: 'right',
                          marginRight: 8,
                        }}
                        title={`${set.imagesDownloaded} of ${set.cardCount} images downloaded`}
                      >
                        {set.imagePercent === 100 ? '✓ imgs' : `${set.imagePercent}% imgs`}
                      </span>
                      {/* Price status */}
                      <span 
                        style={{
                          color: set.pricePercent === 100 ? '#4a4' : set.pricePercent >= 50 ? '#aa4' : '#f55',
                          fontSize: 11,
                          minWidth: 65,
                          textAlign: 'right',
                          marginRight: 8,
                        }}
                        title={`${set.cardsWithPrices} of ${set.cardCount} cards have prices`}
                      >
                        {set.pricePercent === 100 ? '✓ $' : `${set.pricePercent}% $`}
                      </span>
                      {/* Refresh cards from Scryfall button */}
                      <button
                        onClick={async () => {
                          setRefreshingSetCards(set.setCode)
                          setRefreshCardsResult(null)
                          try {
                            const backendUrl = getBackendUrl()
                            const res = await Axios.post(`${backendUrl}/admin/refresh-set-cards/${set.setCode}`, {force: false}, {
                              headers: {'x-admin-token': token},
                              timeout: 300000,
                            })
                            setRefreshCardsResult(res.data)
                            loadStats()
                          } catch (err) {
                            console.error('Failed to refresh cards:', err)
                            setRefreshCardsResult({status: 'error', message: err.response?.data?.error || err.message})
                          } finally {
                            setRefreshingSetCards(null)
                          }
                        }}
                        disabled={refreshingSetCards === set.setCode}
                        title="Re-fetch cards from Scryfall if new cards available"
                        style={{
                          padding: '4px 8px',
                          fontSize: 11,
                          border: 'none',
                          borderRadius: 4,
                          background: refreshingSetCards === set.setCode ? '#555' : '#446',
                          color: '#aaf',
                          cursor: refreshingSetCards === set.setCode ? 'wait' : 'pointer',
                          marginRight: 4,
                        }}>
                        {refreshingSetCards === set.setCode ? '...' : '🔄'}
                      </button>
                      {/* Refresh prices button */}
                      <button
                        onClick={async () => {
                          setRefreshingSet(set.setCode)
                          try {
                            const backendUrl = getBackendUrl()
                            await Axios.post(`${backendUrl}/admin/refresh-set-prices/${set.setCode}`, {}, {
                              headers: {'x-admin-token': token},
                              timeout: 60000,
                            })
                            loadStats()
                          } catch (err) {
                            console.error('Failed to refresh prices:', err)
                          } finally {
                            setRefreshingSet(null)
                          }
                        }}
                        disabled={isRefreshing || refreshingSetPrices}
                        title="Refresh prices from Scryfall"
                        style={{
                          padding: '4px 8px',
                          fontSize: 11,
                          border: 'none',
                          borderRadius: 4,
                          background: isRefreshing ? '#555' : '#444',
                          color: '#aaa',
                          cursor: (isRefreshing || refreshingSetPrices) ? 'wait' : 'pointer',
                        }}>
                        {isRefreshing ? '...' : '💰'}
                      </button>
                    </div>
                  )
                })}
              </div>
              
              {/* Refresh cards result */}
              {refreshCardsResult && (
                <div style={{
                  marginTop: 12,
                  padding: 12,
                  background: refreshCardsResult.status === 'error' ? '#422' : refreshCardsResult.status === 'up_to_date' ? '#2a2a2a' : '#234',
                  borderRadius: 8,
                  fontSize: 13,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}>
                  <span style={{color: refreshCardsResult.status === 'error' ? '#f88' : refreshCardsResult.status === 'updated' ? '#4f4' : '#aaa'}}>
                    {refreshCardsResult.status === 'updated' ? '✅' : refreshCardsResult.status === 'up_to_date' ? '✓' : '❌'} {refreshCardsResult.message}
                  </span>
                  <button
                    onClick={() => setRefreshCardsResult(null)}
                    style={{background: 'none', border: 'none', color: '#666', cursor: 'pointer', fontSize: 16}}>
                    ✕
                  </button>
                </div>
              )}

              {/* Summary of sets needing attention */}
              {(() => {
                const setsNeedingImages = stats.topSets.filter(s => s.imagePercent < 100)
                const setsNeedingPrices = stats.topSets.filter(s => s.pricePercent < 50)
                
                if (setsNeedingImages.length === 0 && setsNeedingPrices.length === 0) return null
                
                return (
                  <div style={{marginTop: 16, padding: 12, background: '#3a2a2a', borderRadius: 8, border: '1px solid #633'}}>
                    {/* Image issues */}
                    {setsNeedingImages.length > 0 && (
                      <div style={{marginBottom: setsNeedingPrices.length > 0 ? 16 : 0}}>
                        <div style={{color: '#fa8', fontSize: 13, marginBottom: 8}}>
                          📥 {setsNeedingImages.length} set{setsNeedingImages.length > 1 ? 's' : ''} with incomplete downloads
                        </div>
                        <div style={{color: '#888', fontSize: 12}}>
                          {setsNeedingImages.map(s => `${s.setCode.toUpperCase()} (${s.imagePercent}%)`).join(', ')}
                        </div>
                      </div>
                    )}
                    
                    {/* Price issues */}
                    {setsNeedingPrices.length > 0 && (
                      <>
                        <div style={{color: '#f88', fontSize: 13, marginBottom: 8}}>
                          💰 {setsNeedingPrices.length} set{setsNeedingPrices.length > 1 ? 's' : ''} missing price data
                        </div>
                        <div style={{color: '#888', fontSize: 12, marginBottom: 12}}>
                          Sets: {setsNeedingPrices.map(s => s.setCode.toUpperCase()).join(', ')}
                        </div>
                        
                        <button
                          onClick={async () => {
                            setRefreshingSetPrices(true)
                            setRefreshPriceResult(null)
                            try {
                              const backendUrl = getBackendUrl()
                              const res = await Axios.post(`${backendUrl}/admin/refresh-all-prices`, {}, {
                                headers: {'x-admin-token': token},
                                timeout: 300000,
                              })
                              setRefreshPriceResult(res.data)
                              loadStats()
                            } catch (err) {
                              setRefreshPriceResult({error: err.response?.data?.error || err.message})
                            } finally {
                              setRefreshingSetPrices(false)
                            }
                          }}
                          disabled={refreshingSetPrices}
                          style={{
                            padding: '10px 20px',
                            fontSize: 13,
                            border: 'none',
                            borderRadius: 6,
                            background: refreshingSetPrices ? '#555' : '#48f',
                            color: '#fff',
                            cursor: refreshingSetPrices ? 'wait' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                          }}>
                          {refreshingSetPrices ? (
                            <>
                              <span style={{
                                width: 14,
                                height: 14,
                                border: '2px solid #fff',
                                borderTopColor: 'transparent',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite',
                              }} />
                              Refreshing prices...
                            </>
                          ) : (
                            '💰 Refresh All Missing Prices'
                          )}
                        </button>
                        
                        {refreshPriceResult && (
                          <div style={{
                            marginTop: 12, 
                            padding: 10, 
                            background: refreshPriceResult.error ? '#422' : '#234', 
                            borderRadius: 6,
                            fontSize: 12,
                          }}>
                            {refreshPriceResult.error ? (
                              <span style={{color: '#f88'}}>❌ {refreshPriceResult.error}</span>
                            ) : (
                              <span style={{color: '#4f4'}}>✅ {refreshPriceResult.message}</span>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )
              })()}
            </div>
          </div>
        )}

        <a
          href="/previews"
          style={{
            display: 'block',
            marginTop: 32,
            textAlign: 'center',
            color: '#888',
            textDecoration: 'none',
          }}>
          ← Back to Previews
        </a>
      </div>
    </div>
  )
}

// Stat box component
const StatBox = ({label, value, highlight, small}) => (
  <div
    style={{
      background: highlight ? '#2a3a2a' : '#2a2a2a',
      padding: 16,
      borderRadius: 8,
      textAlign: 'center',
      border: highlight ? '1px solid #4a4' : 'none',
    }}>
    <div style={{color: '#888', fontSize: 12, marginBottom: 4}}>{label}</div>
    <div style={{color: highlight ? '#6c6' : '#fff', fontSize: small ? 16 : 20, fontWeight: 'bold'}}>{value}</div>
  </div>
)

export default Admin
